/**
 * File picker helpers for the web bridge.
 *
 * - openFilePicker: <input type=file> based, returns file contents (string).
 *   Note: Android docs claim a URI is returned, but Android's bridge has
 *   no implementation; we define the contract as "contents" on web.
 * - saveFileWithPicker: uses File System Access API when available
 *   (Chromium); falls back to a Blob+anchor download elsewhere.
 * - exportModuleData / importModuleData: serialize/restore all of a
 *   module's storage keys as JSON. No picker — pure data ops.
 */

import { storage } from './storage.js';

/**
 * Opens a file picker and resolves with the file's text contents.
 * Resolves to "" if the user cancels.
 */
export function openFilePickerPromise(): Promise<string> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.style.position = 'fixed';
    input.style.left = '-9999px';
    let settled = false;
    const finish = (value: string): void => {
      if (settled) return;
      settled = true;
      try { document.body.removeChild(input); } catch {}
      resolve(value);
    };
    input.addEventListener('change', () => {
      const file = input.files?.[0];
      if (!file) { finish(''); return; }
      const reader = new FileReader();
      reader.onload = () => finish(typeof reader.result === 'string' ? reader.result : '');
      reader.onerror = () => finish('');
      reader.readAsText(file);
    });
    // Detect cancel: when the picker closes without a file, focus returns.
    // We give it a short delay so the 'change' event (if any) lands first.
    window.addEventListener('focus', () => {
      setTimeout(() => { if (!settled && !input.files?.length) finish(''); }, 300);
    }, { once: true });
    document.body.appendChild(input);
    input.click();
  });
}

/**
 * Saves text content to a user-chosen path. Uses showSaveFilePicker on
 * Chromium browsers (proper Save As dialog) and a download fallback elsewhere.
 */
export async function saveFileWithPicker(
  fileName: string,
  mimeType: string,
  content: string,
): Promise<void> {
  // File System Access API (Chromium 86+, Edge, Opera).
  const w = window as unknown as {
    showSaveFilePicker?: (opts: object) => Promise<{
      createWritable: () => Promise<{ write: (data: BlobPart) => Promise<void>; close: () => Promise<void> }>;
    }>;
  };
  if (typeof w.showSaveFilePicker === 'function') {
    try {
      const handle = await w.showSaveFilePicker({
        suggestedName: fileName,
        types: [{ description: 'File', accept: { [mimeType || 'text/plain']: ['.' + (fileName.split('.').pop() || 'txt')] } }],
      });
      const writable = await handle.createWritable();
      await writable.write(new Blob([content], { type: mimeType || 'text/plain' }));
      await writable.close();
      return;
    } catch (err) {
      // AbortError = user cancelled, treat as a no-op
      if ((err as Error).name === 'AbortError') return;
      console.warn('[files] showSaveFilePicker failed, falling back', err);
    }
  }
  // Fallback: trigger a download via an anchor.
  const blob = new Blob([content], { type: mimeType || 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Returns a JSON map of {key: value} for every storage entry under
 * moduleId, EXCLUDING internal namespaces (__published__::, dot-prefixed).
 */
export function exportModuleData(moduleId: string): string {
  const out: Record<string, string> = {};
  for (const key of storage.listKeysForModule(moduleId)) {
    if (key.startsWith('__published__::') || key.startsWith('.')) continue;
    out[key] = storage.get({ moduleId, key });
  }
  return JSON.stringify(out);
}

/**
 * Restores storage entries from a JSON map. Existing keys are overwritten.
 * Returns the number of keys imported.
 */
export function importModuleData(moduleId: string, json: string): number {
  let data: unknown;
  try { data = JSON.parse(json); } catch { return 0; }
  if (!data || typeof data !== 'object' || Array.isArray(data)) return 0;
  let count = 0;
  for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
    if (typeof value !== 'string') continue;
    if (key.startsWith('__published__::') || key.startsWith('.')) continue;
    storage.set({ moduleId, key }, value);
    count++;
  }
  return count;
}
